import React, { useState } from 'react';
import { Check } from 'lucide-react';

export interface ChecklistItem {
  index?: number;
  label: string;
  done: boolean;
  status?: string;
}

export interface ActivationChecklistProps {
  items: ChecklistItem[];
  /** When provided, checklist rows become interactive toggles. */
  onToggle?: (item: ChecklistItem, nextDone: boolean) => void | Promise<void>;
  disabled?: boolean;
}

export const ActivationChecklist: React.FC<ActivationChecklistProps> = ({
  items,
  onToggle,
  disabled = false,
}) => {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const done = items.filter((item) => item.done).length;
  const interactive = typeof onToggle === 'function';

  const handleToggle = async (item: ChecklistItem) => {
    if (!interactive || disabled || pendingKey) return;
    const key = String(item.index ?? item.label);
    setPendingKey(key);
    try {
      await onToggle(item, !item.done);
    } finally {
      setPendingKey(null);
    }
  };

  return (
    <>
      <ul className="space-y-2">
        {items.map((item) => {
          const key = String(item.index ?? item.label);
          const isPending = pendingKey === key;
          const rowDisabled = disabled || Boolean(pendingKey);
          const content = (
            <>
              <span
                className={`w-5 h-5 rounded-md flex items-center justify-center shrink-0 transition-colors ${
                  item.done
                    ? 'bg-emerald-500 text-white'
                    : 'bg-white border border-[#cbd5e1]'
                } ${interactive && !rowDisabled ? 'group-hover:border-[#18365E]' : ''}`}
              >
                {isPending ? (
                  <span className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                ) : (
                  item.done && <Check className="w-3 h-3" />
                )}
              </span>
              <span className={item.done ? 'text-[#4b5563]' : 'text-[#717182]'}>{item.label}</span>
            </>
          );

          return (
            <li key={key} className="text-xs">
              {interactive ? (
                <button
                  type="button"
                  onClick={() => { void handleToggle(item); }}
                  disabled={rowDisabled}
                  aria-pressed={item.done}
                  className={`group w-full flex items-center gap-2.5 text-left rounded-md px-0.5 py-0.5 -mx-0.5 transition-colors ${
                    rowDisabled
                      ? 'opacity-70 cursor-wait'
                      : 'hover:bg-[#F5F7FA] cursor-pointer'
                  }`}
                >
                  {content}
                </button>
              ) : (
                <div className="flex items-center gap-2.5">{content}</div>
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] text-[#717182] mt-3">
        {done} of {items.length} complete
      </p>
    </>
  );
};

export default ActivationChecklist;
